import {
  type DataGraphProps,
  type GraphEdge,
  GraphLayout,
  type GraphNode,
  type GraphNodeField,
  type GraphNodeStyleName,
  type PageFormat,
} from "@/components-v2/DataGraph/dataGraphDomain";
import {
  type DCEvent,
  type HttpEvent,
  type HttpResponse,
  httpGet,
  httpPost,
} from "@/events/eventsDomain";
import type { TMessage, TMessageParam } from "@/i18n/tt";
import type { TMessageKey } from "@/i18n/types.gen";
import type { OnboardingCandidateResponse } from "@/stores/PortfolioStore";
import { orderlessFilter } from "@/utils/filter";
import { formatOrgNum } from "@/utils/other";
import { compact, maxBy } from "lodash";
import { OWNERSHIP_DECIMALS } from "@/shareholderRegister/transactions/ownersPerDate/ownersPerDateDomain";
import type {
  AskPermissionRequest,
  CollapseSettings,
  GraphNodeSettings,
  OwnershipEdge,
  OwnershipGraph,
  OwnershipNode,
} from "./ownershipDomain";
import { filterOwnershipGraph, type OwnershipFilterGroup } from "./ownershipFilterDomain";

export enum OwnershipGraphType {
  owns = "owns",
  owner = "owner",
  dual = "dual",
  bo = "bo",
  bo_aml = "bo_aml",
}

const ownershipGraphTypePaths: Record<OwnershipGraphType, string> = {
  [OwnershipGraphType.owns]: OwnershipGraphType.owns,
  [OwnershipGraphType.owner]: OwnershipGraphType.owner,
  [OwnershipGraphType.dual]: OwnershipGraphType.dual,
  [OwnershipGraphType.bo]: "beneficial-owners",
  [OwnershipGraphType.bo_aml]: "beneficial-owners-aml",
};

export const ownershipGraphTypeToPath = (graphType: OwnershipGraphType): string =>
  ownershipGraphTypePaths[graphType];

export const isBeneficialOwners = (graphType: OwnershipGraphType): boolean =>
  graphType === OwnershipGraphType.bo || graphType === OwnershipGraphType.bo_aml;

export enum MapOutputFormat {
  PDF = "PDF",
  SVG = "SVG",
}

export type SubsidiaryMapResponse = {
  threshold: number;
  maxnum: number;
  ownershipGraphType: OwnershipGraphType;
  format: MapOutputFormat;
};

export const ownershipApi = {
  getTrankSubsidiariesMap: ({
    id,
    params,
    layout,
    onSuccess,
    onFailure,
  }: {
    id: string;
    params: SubsidiaryMapResponse;
    layout: GraphLayout;
    onSuccess: (res: HttpResponse<string>) => DCEvent[];
    onFailure: () => DCEvent[];
  }): HttpEvent => {
    const query = new URLSearchParams({
      threshold: String(params.threshold),
      maxnum: String(params.maxnum),
      ownershipGraphType: params.ownershipGraphType,
      layout,
      format: params.format,
    }).toString();
    return httpGet({
      url: `/company/${id}/map?${query}`,
      headers: params.format === MapOutputFormat.SVG ? { Accept: "image/svg+xml" } : undefined,
      responseType: params.format === MapOutputFormat.PDF ? "blob" : undefined,
      onSuccess,
      onFailure,
    });
  },

  getOwnershipGraph: ({
    id,
    type,
    threshold,
    maxnum,
    onSuccess,
    onFailure,
  }: {
    id: string;
    type: OwnershipGraphType;
    threshold: number;
    maxnum: number;
    onSuccess: (res: HttpResponse<OwnershipGraph>) => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }): HttpEvent => {
    const query = new URLSearchParams({
      threshold: String(threshold),
      maxnum: String(maxnum),
    }).toString();
    return httpGet({
      url: `/company/ownership-graph/${id}/${type}?${query}`,
      onSuccess,
      onFailure,
    });
  },

  findOnboardingCandidates: ({
    orgNo,
    onSuccess,
    onFailure,
  }: {
    orgNo: string;
    onSuccess: (res: HttpResponse<OnboardingCandidateResponse>) => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }) =>
    httpGet({
      url: `/company-roles/find-onboarding-candidates/${orgNo}`,
      onSuccess,
      onFailure,
    }),

  sendAskForPermissionRequest: ({
    data,
    onSuccess,
    onFailure,
  }: {
    data: AskPermissionRequest;
    onSuccess: (res: HttpResponse<void>) => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }): HttpEvent => {
    return httpPost({ url: `/invitation/ask-for-permission`, data, onSuccess, onFailure });
  },
};

export type OwnershipNodeStyle = "root" | "controlled" | "person" | "company" | "group";

const OWNERSHIP_NODE_COLORS: Record<OwnershipNodeStyle, GraphNodeStyleName> = {
  root: "contrast",
  controlled: "green",
  person: "orange",
  company: "gray",
  group: "gray",
};

export const ownershipNodeStyle = (node: OwnershipNode): OwnershipNodeStyle =>
  node.isRoot
    ? "root"
    : node.isGroup
      ? "group"
      : (node.votingPower ?? 0) >= 1
        ? "controlled"
        : node.isCompany
          ? "company"
          : "person";

const nodeIdentity = (node: OwnershipNode): GraphNodeField | undefined => {
  if (node.isGroup) return undefined;
  const value = compact([
    node.country,
    node.isCompany ? formatOrgNum(node.organizationNumber) : node.birthDate,
  ]).join(" ");
  return value ? { value } : undefined;
};

export const OWNERSHIP_PERCENT_OPTIONS = { maximumFractionDigits: OWNERSHIP_DECIMALS };

const ownershipNodeFields = (node: OwnershipNode): GraphNodeField[] => {
  const identity = nodeIdentity(node);
  if (node.isRoot) return compact([identity]);
  const { votingPower, integrated } = node;
  return compact([
    identity,
    votingPower !== undefined && votingPower >= 1
      ? {
          value: { type: "percent", value: votingPower, ...OWNERSHIP_PERCENT_OPTIONS },
          align: "right",
        }
      : undefined,
    integrated !== undefined
      ? {
          value: ["tag", { type: "percent", value: integrated, ...OWNERSHIP_PERCENT_OPTIONS }],
          align: "right",
        }
      : undefined,
  ]);
};

type RouteType = OwnershipGraphType.owner | OwnershipGraphType.owns;
const ROUTE_TYPES: RouteType[] = [OwnershipGraphType.owner, OwnershipGraphType.owns];
type Holding = { companyId: string; share: number };
type OwnershipRoute = { ownership: number; between: string[] };

const holdingsTowardRoot = (edges: OwnershipEdge[], id: string, type: RouteType): Holding[] =>
  edges
    .filter((e) => (type === OwnershipGraphType.owner ? e.source : e.target) === id)
    .map((e) => ({
      companyId: type === OwnershipGraphType.owner ? e.target : e.source,
      share: e.directOwnership ?? 0,
    }));

function highestOwnershipRoute(
  edges: OwnershipEdge[],
  fromId: string,
  rootId: string,
  type: RouteType,
  visited: Set<string>,
): OwnershipRoute | undefined {
  if (fromId === rootId) return { ownership: 1, between: [] };
  const routes = compact(
    holdingsTowardRoot(edges, fromId, type)
      .filter((holding) => !visited.has(holding.companyId))
      .map((holding) => {
        const rest = highestOwnershipRoute(
          edges,
          holding.companyId,
          rootId,
          type,
          new Set([...visited, holding.companyId]),
        );
        return (
          rest && {
            ownership: holding.share * rest.ownership,
            between: holding.companyId === rootId ? [] : [holding.companyId, ...rest.between],
          }
        );
      }),
  );
  return maxBy(routes, (route) => route.ownership);
}

export function ownershipRouteToRoot(
  graph: OwnershipGraph,
  nodeId: string,
): { type: RouteType; between: OwnershipNode[] } | undefined {
  const root = graph.nodes.find((n) => n.isRoot);
  if (!root || nodeId === root.id) return undefined;
  const visited = new Set([nodeId]);
  const routes = compact(
    ROUTE_TYPES.map((type) => {
      const route = highestOwnershipRoute(graph.edges, nodeId, root.id, type, visited);
      return route && { type, route };
    }),
  );
  const strongest = maxBy(routes, (candidate) => candidate.route.ownership);
  if (!strongest) return undefined;
  const between = compact(
    strongest.route.between.map((id) => graph.nodes.find((n) => n.id === id)),
  );
  return {
    type: strongest.type,
    between: strongest.type === OwnershipGraphType.owns ? between.reverse() : between,
  };
}

type DataSource = {
  id: string;
  title?: TMessageKey;
  description: TMessageKey;
  matches: (code: string) => boolean;
};

const oneOf =
  (...codes: string[]) =>
  (code: string) =>
    codes.includes(code);

const DATA_SOURCES: DataSource[] = [
  {
    id: "account",
    description: "ownership.details.data-source.account",
    matches: oneOf("account"),
  },
  {
    id: "dcompany",
    title: "common.dcompany",
    description: "ownership.details.data-source.dcompany",
    matches: oneOf("dc"),
  },
  {
    id: "international",
    title: "ownership.data-source.international-register",
    description: "ownership.details.data-source.international-register",
    matches: oneOf("v8", "t"),
  },
  {
    id: "tax",
    title: "ownership.data-source.shareholder-register",
    description: "ownership.details.data-source.shareholder-register",
    matches: (code) => code.startsWith("exp"),
  },
  {
    id: "brreg",
    title: "ownership.data-source.brreg",
    description: "ownership.details.data-source.brreg",
    matches: oneOf("brreg"),
  },
  {
    id: "vps",
    title: "common.euronext-securities-oslo-vps",
    description: "common.euronext-securities-oslo-vps",
    matches: oneOf("vps"),
  },
  {
    id: "cvr",
    title: "ownership.data-source.cvr",
    description: "ownership.details.data-source.cvr",
    matches: oneOf("cvr"),
  },
  {
    id: "gru",
    title: "ownership.data-source.gru",
    description: "ownership.details.data-source.gru",
    matches: oneOf("gru"),
  },
  {
    id: "ft",
    title: "ownership.data-source.ft",
    description: "ownership.details.data-source.ft",
    matches: oneOf("ft"),
  },
];

export const dataSourceOf = (code?: string): DataSource | undefined =>
  code === undefined ? undefined : DATA_SOURCES.find((source) => source.matches(code));

export const dataSourceTitle = (node: OwnershipNode): TMessage | undefined => {
  const source = dataSourceOf(node.ownershipSource);
  if (source?.title) return { key: source.title };
  return node.enterpriseAccountName ? { translated: node.enterpriseAccountName } : undefined;
};

export function ownershipSourceSentence(node: OwnershipNode): TMessage | undefined {
  const key = dataSourceOf(node.ownershipSource)?.description;
  if (!key) return undefined;
  const accountName = node.enterpriseAccountName;
  const source: TMessage = accountName ? { key, params: { accountName } } : { key };
  return { key: "ownership.details.data-source", params: { source } };
}

export const toOwnershipList = (graph: OwnershipGraph | undefined): OwnershipNode[] =>
  (graph?.nodes ?? []).filter((node) => !node.isRoot && !node.isGroup);

const SENTENCE_KEYS_BY_ROUTE_TYPE: Record<
  RouteType,
  { direct: TMessageKey; indirect: TMessageKey }
> = {
  [OwnershipGraphType.owner]: {
    direct: "ownership.details.relation.direct-owner",
    indirect: "ownership.details.relation.indirect-owner",
  },
  [OwnershipGraphType.owns]: {
    direct: "ownership.details.relation.direct-subsidiary",
    indirect: "ownership.details.relation.indirect-subsidiary",
  },
};

export function ownershipRelationSentence(
  node: OwnershipNode,
  graph: OwnershipGraph,
): TMessage | undefined {
  const root = graph.nodes.find((n) => n.isRoot);
  const route = ownershipRouteToRoot(graph, node.id);
  if (!root || !route || node.isGroup) return undefined;
  const keys = SENTENCE_KEYS_BY_ROUTE_TYPE[route.type];
  const params = { entity: node.name, company: root.name };
  if (route.between.length === 0) return { key: keys.direct, params };
  const intermediates: TMessageParam = { type: "list", value: route.between.map((n) => n.name) };
  return { key: keys.indirect, params: { ...params, intermediates } };
}

const getTooltipTitle = (node: OwnershipNode): TMessage => ({
  key: "ownership.graph.tooltip.title",
  params: {
    name: node.name,
    country: node.country,
    number: node.organizationNumber && { type: "org-no", value: node.organizationNumber },
  },
});

const tooltipFigure = (key: TMessageKey, value: number | undefined): TMessage | undefined =>
  value === undefined
    ? undefined
    : { key, params: { percent: { type: "percent", value, ...OWNERSHIP_PERCENT_OPTIONS } } };

const getTooltipFigures = (node: OwnershipNode): TMessageParam | undefined => {
  const figures = compact([
    tooltipFigure("ownership.graph.tooltip.integrated-ownership", node.integrated),
    tooltipFigure("ownership.graph.tooltip.voting-power", node.votingPower),
  ]);
  return figures.length > 0 ? { type: "list", separator: "\n", value: figures } : undefined;
};

export function ownershipNodeTooltip(graph: OwnershipGraph, id: string): string | TMessageParam {
  const node = graph.nodes.find((n) => n.id === id);
  if (!node || node.isGroup) return "";
  return {
    type: "list",
    separator: "\n\n",
    value: compact([
      getTooltipTitle(node),
      getTooltipFigures(node),
      ownershipRelationSentence(node, graph),
      ownershipSourceSentence(node),
    ]),
  };
}

export const ownershipGraphNode: GraphNode<OwnershipNode> = {
  getId: (n) => n.id,
  getLabel: (n) => (n.isGroup ? { key: "shareholders.unidentified" } : n.name),
  getFields: ownershipNodeFields,
  getStyle: (n) => OWNERSHIP_NODE_COLORS[ownershipNodeStyle(n)],
  getIcon: (n) => (n.isGroup ? "city" : n.isCompany ? "buildings" : "user"),
  getBadges: (n) =>
    n.isInDcompany &&
    (n.ownershipSource === "dc" || n.ownershipSource === "account") &&
    !n.isRoot &&
    !n.isGroup
      ? ["dCompany"]
      : [],
  getGrouped: (n) => n.isGroup ?? false,
};

const ownershipEdgeLabel = (e: OwnershipEdge): string | TMessage | TMessageParam => {
  if (e.directOwnership === undefined) return "";
  const ownership = {
    type: "percent",
    value: e.directOwnership,
    ...OWNERSHIP_PERCENT_OPTIONS,
  } as const;
  if (e.votingRights === undefined || e.votingRights === e.directOwnership) return ownership;
  return {
    key: "ownership.graph.edge.ownership-and-voting",
    params: {
      ownership,
      voting: { type: "percent", value: e.votingRights, ...OWNERSHIP_PERCENT_OPTIONS },
    },
  };
};

export const ownershipGraphEdge: GraphEdge<OwnershipEdge> = {
  getId: (e) => e.id,
  getSource: (e) => e.source,
  getTarget: (e) => e.target,
  getLabel: ownershipEdgeLabel,
};

const ownershipNodeSearchValues = (n: OwnershipNode) =>
  compact([n.name, n.isCompany ? n.organizationNumber : n.birthDate]);

export const matchOwnershipNodes = (nodes: OwnershipNode[], query: string): OwnershipNode[] =>
  orderlessFilter(nodes, ownershipNodeSearchValues, query);

export const hiddenChildrenOf = (settings?: CollapseSettings): "all" | string[] | undefined => {
  if (settings?.collapse === "collapsed") return "all";
  if (settings?.collapse === "custom") return settings.hiddenChildren ?? [];
  return undefined;
};

export const ownershipGraphLayout = (
  graph: OwnershipGraph | undefined,
  nodeSettings: { [id: string]: GraphNodeSettings } | undefined,
): GraphLayout => {
  const rootId = graph?.nodes.find((node) => node.isRoot)?.id;
  return (rootId ? nodeSettings?.[rootId]?.layout : undefined) ?? GraphLayout.TopDown;
};

export const ownershipGraphProps = ({
  graph,
  graphType,
  filterGroup,
  pageFormat,
  nodeSettings,
}: {
  graph: OwnershipGraph;
  graphType: OwnershipGraphType;
  filterGroup?: OwnershipFilterGroup;
  pageFormat?: PageFormat;
  nodeSettings: { [id: string]: GraphNodeSettings };
}): DataGraphProps<OwnershipNode, OwnershipEdge> => {
  const shown = filterOwnershipGraph(
    graph,
    isBeneficialOwners(graphType) ? undefined : filterGroup,
  );
  return {
    nodeData: shown.nodes,
    edgeData: shown.edges,
    layout: ownershipGraphLayout(graph, nodeSettings),
    pageFormat,
    originId: graph.nodes.find((node) => node.isRoot)?.id,
    highlightPath: true,
    getTooltip: (id) => ownershipNodeTooltip(graph, id),
    graphNode: {
      ...ownershipGraphNode,
      getDimmed: (n) => shown.idsConnectingToRoot.has(n.id),
      getPathEndpoint: (n) => n.isRoot ?? false,
      getBranchLayout: (n) => nodeSettings[n.id]?.layout,
      getHiddenChildren: (n) => hiddenChildrenOf(nodeSettings[n.id]),
    },
    graphEdge: ownershipGraphEdge,
  };
};
